import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { toast } from 'component-library';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { postRequest } from '@/api/rest/hooks/requests/post';
import { usePostExportReportCsv } from '@/api/rest/resources/report';
import {
  AutomatedReport,
  AutomatedReportStatus,
  GET_AUTOMATED_REPORT_DETAIL_PATH,
  GET_AVAILABLE_AUTOMATED_REPORTS_PATH,
  GET_AVAILABLE_CSV_EXPORTS_PATH,
  GET_ORGANIZATION_AUTOMATED_REPORTS_PATH,
  GET_REPORTS_EXPORT_FACTS_PATH,
  GET_REPORTS_SUBSCRIBED_INDICATORS_PATH,
  GetAutomatedReportDetailErrorDto,
  GetAutomatedReportDetailRequestPathVariables,
  GetAutomatedReportDetailSuccessDto,
  GetAvailableAutomatedReportsErrorDto,
  GetAvailableAutomatedReportsRequestPathVariables,
  GetAvailableAutomatedReportsSuccessDto,
  GetAvailableCsvExportsErrorDto,
  GetAvailableCsvExportsRequestPathVariables,
  GetAvailableCsvExportsSuccessDto,
  GetOrganizationAutomatedReportsErrorDto,
  GetOrganizationAutomatedReportsRequestPathVariables,
  GetOrganizationAutomatedReportsSuccessDto,
  GetReportsExportFactsErrorDto,
  GetReportsExportFactsSuccessDto,
  GetReportsSubscribedIndicatorsErrorDto,
  GetReportsSubscribedIndicatorsSuccessDto,
  POST_ORGANIZATION_GENERATE_REPORT_TYPE_PATH,
  PostOrganizationGenerateReportRequestTypePathVariables,
  PostOrganizationGenerateReportTypeErrorDto,
  PostOrganizationGenerateReportTypeRequestBodyData,
  PostOrganizationGenerateReportTypeSuccessDto,
  REPORT_TYPES,
} from '@/api/rest/resources/types/reports';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';
import { downloadFromUrl } from '@/utils/export';

import { useOrganizationId } from './useOrganizationId';

export const useReportsSubscribedIndicators = (organizationIdArg?: string, limit?: number, offset?: number) => {
  const orgId = useOrganizationId();

  const queryParams = {
    organization_id: organizationIdArg ?? orgId,
    limit,
    offset,
  };

  const queryFn = () =>
    getRequest<GetReportsSubscribedIndicatorsSuccessDto>(
      GET_REPORTS_SUBSCRIBED_INDICATORS_PATH,
      undefined,
      queryParams,
    );

  return useSuspenseQuery<GetReportsSubscribedIndicatorsSuccessDto, GetReportsSubscribedIndicatorsErrorDto>({
    queryKey: ['reports', 'subscribed_indicators', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useReportsExportFacts = (factName: string, organizationIdArg?: string, plotGroupIdArg?: string | null) => {
  const orgId = useOrganizationId();

  const queryParams = {
    organization_id: organizationIdArg ?? orgId,
    fact_name: factName,
    plot_group_id: isNullish(plotGroupIdArg) ? undefined : plotGroupIdArg,
  };

  const queryFn = () =>
    getRequest<GetReportsExportFactsSuccessDto>(GET_REPORTS_EXPORT_FACTS_PATH, undefined, queryParams, undefined, {
      responseType: 'blob',
    });

  return useQuery<GetReportsExportFactsSuccessDto, GetReportsExportFactsErrorDto>({
    queryKey: ['reports', 'export_facts', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
    enabled: false,
  });
};

export const useGenerateReportByType = ({
  reportType,
  organizationIdArg,
  onSuccess,
}: {
  reportType: (typeof REPORT_TYPES)[keyof typeof REPORT_TYPES];
  organizationIdArg?: string;
  onSuccess?: (data: AutomatedReport) => void;
}) => {
  const orgId = useOrganizationId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const plotGroupId = usePlotGroupId();

  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');
  assertIsDefined(reportType, 'reportType');

  const mutationFn = () => {
    const pathVariables: PostOrganizationGenerateReportRequestTypePathVariables = {
      organization_id: organizationId,
      report_type: reportType,
    };

    const bodyData: PostOrganizationGenerateReportTypeRequestBodyData = {
      plot_group_id: plotGroupId ?? undefined,
    };

    return postRequest<PostOrganizationGenerateReportTypeSuccessDto>(
      POST_ORGANIZATION_GENERATE_REPORT_TYPE_PATH,
      pathVariables,
      undefined,
      bodyData,
    );
  };

  const reportLabel = t(`reports.reportTypes.${reportType}`);

  return useMutation<PostOrganizationGenerateReportTypeSuccessDto, PostOrganizationGenerateReportTypeErrorDto>({
    mutationFn,
    onSuccess: (data) => {
      onSuccess?.(data);

      queryClient.invalidateQueries({ queryKey: ['organization', organizationId, 'automated_reports'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'reports'] });
      toast({
        title: t('reports.toasts.generationStarted', { reportType: reportLabel }),
        type: 'default',
      });
    },
    onError: () => {
      toast({ title: t('reports.toasts.generationFailed', { reportType: reportLabel }), type: 'error' });
    },
  });
};

export const useOrganizationAutomatedReports = () => {
  const orgId = useOrganizationId();

  const queryFn = () => {
    const pathVariables: GetOrganizationAutomatedReportsRequestPathVariables = {
      organization_id: orgId,
    };
    return getRequest<GetOrganizationAutomatedReportsSuccessDto>(
      GET_ORGANIZATION_AUTOMATED_REPORTS_PATH,
      pathVariables,
      undefined,
    );
  };

  return useQuery<GetOrganizationAutomatedReportsSuccessDto, GetOrganizationAutomatedReportsErrorDto>({
    queryKey: ['organization', orgId, 'automated_reports'],
    queryFn,
    refetchInterval: 5000,
  });
};

export const useAvailableCsvExports = (organizationIdArg?: string) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryFn = () => {
    const pathVariables: GetAvailableCsvExportsRequestPathVariables = {
      organization_id: organizationId,
    };
    return getRequest<GetAvailableCsvExportsSuccessDto>(GET_AVAILABLE_CSV_EXPORTS_PATH, pathVariables);
  };

  return useSuspenseQuery<GetAvailableCsvExportsSuccessDto, GetAvailableCsvExportsErrorDto>({
    queryKey: ['organization', organizationId, 'available_csv_exports'],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useAvailableAutomatedReports = (organizationIdArg?: string) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryFn = () => {
    const pathVariables: GetAvailableAutomatedReportsRequestPathVariables = {
      organization_id: organizationId,
    };
    return getRequest<GetAvailableAutomatedReportsSuccessDto>(GET_AVAILABLE_AUTOMATED_REPORTS_PATH, pathVariables);
  };

  return useSuspenseQuery<GetAvailableAutomatedReportsSuccessDto, GetAvailableAutomatedReportsErrorDto>({
    queryKey: ['organization', organizationId, 'available_automated_reports'],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useExportReportCsv = () => {
  const orgId = useOrganizationId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const plotGroupId = usePlotGroupId();

  const { mutate: baseMutate, ...rest } = usePostExportReportCsv();

  const mutate = ({ reportType }: { reportType: string }) => {
    baseMutate(
      {
        pathVariables: { organization_id: orgId, report_type: reportType },
        bodyData: { plot_group_id: plotGroupId ?? undefined },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['organization', orgId, 'automated_reports'] });
          queryClient.invalidateQueries({ queryKey: ['organization', 'reports'] });
          toast({
            title: t('reports.csvExport.generationStarted'),
            type: 'default',
          });
        },
        onError: () => {
          toast({
            title: t('reports.csvExport.generationFailed'),
            type: 'error',
          });
        },
      },
    );
  };

  return { mutate, ...rest };
};

export const useAutomatedReportDetail = (reportId: string | null) => {
  const queryFn = () => {
    assertIsDefined(reportId, 'reportId');
    const pathVariables: GetAutomatedReportDetailRequestPathVariables = {
      report_id: reportId,
    };
    return getRequest<GetAutomatedReportDetailSuccessDto>(GET_AUTOMATED_REPORT_DETAIL_PATH, pathVariables);
  };

  return useQuery<GetAutomatedReportDetailSuccessDto, GetAutomatedReportDetailErrorDto>({
    queryKey: ['automated_report', reportId],
    queryFn,
    enabled: !!reportId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === AutomatedReportStatus.SUCCESS || status === AutomatedReportStatus.FAILED) return false;
      return 3000;
    },
  });
};

export const useTriggerCsvExport = () => {
  const orgId = useOrganizationId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const plotGroupId = usePlotGroupId();
  const [pendingReportId, setPendingReportId] = useState<string | null>(null);

  const { mutate: baseMutate, isPending } = usePostExportReportCsv();

  const triggerExport = (reportType: string) => {
    setPendingReportId(null);
    baseMutate(
      {
        pathVariables: { organization_id: orgId, report_type: reportType },
        bodyData: { plot_group_id: plotGroupId ?? undefined },
      },
      {
        onSuccess: (data) => {
          setPendingReportId(data.id);
          queryClient.invalidateQueries({ queryKey: ['organization', orgId, 'automated_reports'] });
          queryClient.invalidateQueries({ queryKey: ['organization', 'reports'] });
          toast({
            title: t('reports.csvExport.generationStarted'),
            type: 'default',
          });
        },
        onError: () => {
          toast({
            title: t('reports.csvExport.generationFailed'),
            type: 'error',
          });
        },
      },
    );
  };

  const clearPendingReport = useCallback(() => setPendingReportId(null), []);

  return { triggerExport, pendingReportId, isPending, clearPendingReport };
};

export const useAutoDownloadReport = (reportId: string | null, reportName: string, onComplete?: () => void) => {
  const { t } = useTranslation();
  const { data: reportDetail } = useAutomatedReportDetail(reportId);

  useEffect(() => {
    if (!reportId) return;

    if (reportDetail?.status === AutomatedReportStatus.SUCCESS && reportDetail?.download_url) {
      downloadFromUrl(reportDetail.download_url);
      toast({
        title: t('reports.toasts.reportReady', { reportType: reportName }),
        type: 'success',
      });
      onComplete?.();
    }

    if (reportDetail?.status === AutomatedReportStatus.FAILED) {
      toast({
        title: t('reports.dataExport.exportError.title'),
        description: t('reports.dataExport.exportError.description', { indicatorName: reportName }),
        type: 'error',
      });
      onComplete?.();
    }
  }, [reportDetail, reportId, t, reportName, onComplete]);

  return { isPolling: reportId !== null };
};

export const useCsvExportWithPolling = (reportName: string) => {
  const { triggerExport, pendingReportId, isPending, clearPendingReport } = useTriggerCsvExport();
  const { isPolling } = useAutoDownloadReport(pendingReportId, reportName, clearPendingReport);

  const isLoading = isPending || isPolling;

  return { triggerExport, isLoading };
};
